import { useTour } from '@reactour/tour';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useModal } from '@/components/common/modal';
import { PlusIcon } from '@/components/common/plus-icon';
import { Table } from '@/components/common/table';
import { TopBar } from '@/components/common/top-bar';
import { Button, ButtonVariant } from '@/components/form/button';
import { SearchInput } from '@/components/form/search-input';
import { useAuth } from '@/features/auth';
import { getTourStep } from '@/features/demo/steps';
import { CreateAnalysisWizard } from '@/features/single-measurement';
import { IBatch, Method, State } from '@/features/single-measurement/types';
import { useInfinitePagination } from '@/hooks/use-infinite-pagination';
import { useRoute } from '@/hooks/use-route';
import { MeasurementOverviewLayout } from '@/layouts';
import { getAdminGroup } from '@/utils/get-admin-group';

import { CompleteFermentationModal } from '../components/complete-fermentation-modal';
import { CreateFermentationModal } from '../components/create-fermentation-modal';
import { RemoveFermentationModal } from '../components/remove-fermentation-modal';
import {
  ProvideFermentationAction,
  useFermentationAction,
} from '../hooks/use-fermentation-action';
import { getFermentations } from '../services/fermentations';
import { FermentationQueryKeys } from '../services/query-keys';
import { IFermentation } from '../types/fermentation';
import { fermentationColumns } from '../utils/fermentation-columns';

const TrackingOverview = () => {
  const { t } = useTranslation();
  const theme: any = useTheme();
  const { routeToFermentationBatch } = useRoute();
  const { state: fermentationActionState, resetModals } = useFermentationAction();
  const { isOpen: fermentationIsOpen, toggle: fermentationToggle } = useModal();
  const { routeToHome, routeToFermentation } = useRoute();

  // demo
  const {
    isOpen: isTourOpen,
    setIsOpen: setIsTourOpen,
    setCurrentStep,
    steps,
  } = useTour();
  const [wasTourOpen, setWasTourOpen] = useState<Boolean>(false);

  // a trick to wait until the data is loaded
  useEffect(() => {
    setWasTourOpen(isTourOpen); // remember the tour state
    setIsTourOpen(false); // temp turn off the tour, once data is loaded then we can set it open
  }, []);

  // state filter
  const initialStates = [
    State.FermentationOngoing,
    State.FermentationComplete,
    State.UserFermentationCompleted,
  ];
  const [states, _] = useState<State[]>(initialStates);

  // determine analysis method based on signed in method
  const { user } = useAuth();
  const analysisMethod = useMemo(() => {
    if (user?.signedInMethod === 'wine') return Method.WineConzViab;
    if (user?.signedInMethod === 'beer') return Method.Yeast2ConzViab;
    if (user?.signedInMethod === 'ethanol') return Method.BioEthConzViab;
    return Method.WineConz;
  }, [user]);

  // search by name filter
  const [nameFilter, setNameFilter] = useState('');

  // get admin role
  const group = useMemo(() => getAdminGroup(user?.roles), [user]);

  // query data
  // right now we only fetch 'result ready' single measurements
  const query = useInfiniteQuery({
    queryKey: [FermentationQueryKeys.GetAllFermentations, states, nameFilter],
    queryFn: ({ pageParam }) =>
      getFermentations({
        states,
        page: pageParam,
        method: analysisMethod,
        name: nameFilter,
        group,
      }),
    keepPreviousData: true,
    onSuccess: () => {
      // if tour was open, then we open the tour again
      if (wasTourOpen) {
        setIsTourOpen(true);
        setCurrentStep(getTourStep(steps, 'OkjY32s'));
      }
    },
  });
  const { data, state, fetchNextPage } = useInfinitePagination<IFermentation>({ query });

  // apply search filter
  const onSearch = (search: string) => {
    setNameFilter(search);
    if (search === '') query.refetch(); // refetch in case user clear out all
  };

  const handleOnRowClick = (data: IFermentation) => {
    routeToFermentation(data.id);
    return;
  };

  // once batch is created under the selected fermentation, this method will be called
  const handleSuccessfulBatchCreation = (batch: IBatch) => {
    toast.success('Batch has been added to the fermentation');
    routeToFermentationBatch({ batchId: batch.id, fermentationId: batch.parent! });
  };

  return (
    <>
      <CreateFermentationModal isOpen={fermentationIsOpen} toggle={fermentationToggle} />
      <MeasurementOverviewLayout>
        <RemoveFermentationModal
          isOpen={fermentationActionState.operation === 'remove'}
          toggle={() => resetModals()}
        />
        <CompleteFermentationModal
          isOpen={fermentationActionState.operation === 'complete'}
          toggle={() => resetModals()}
        />
        <CreateAnalysisWizard
          close={() => resetModals()}
          fermentationId={fermentationActionState.id}
          isOpen={fermentationActionState.operation === 'add'}
          onSuccess={handleSuccessfulBatchCreation}
        />
        <TopBar onBack={routeToHome}>
          <Button
            variant={ButtonVariant.Link}
            classnames="flex items-center gap-2"
            onClick={fermentationToggle}
          >
            <PlusIcon />
            <span className={`text-sm font-semibold text-${theme.current}-primary`}>
              {t('common.new')}
            </span>
          </Button>
        </TopBar>

        <MeasurementOverviewLayout.Title title="fermentations.title">
          <SearchInput onSearch={onSearch} isLoading={query.isFetching} />
        </MeasurementOverviewLayout.Title>

        <div id="step-fermentation-overview">
          <MeasurementOverviewLayout.Body>
            <Table<IFermentation>
              data={data}
              columns={fermentationColumns}
              fetchNextPage={fetchNextPage}
              hiddenColumnIds={group ? [] : ['email']} // hide nothing if admin, otherwise hide email
              state={state}
              onRowClick={handleOnRowClick}
            />
          </MeasurementOverviewLayout.Body>
        </div>
      </MeasurementOverviewLayout>
    </>
  );
};

// we have to wrap the page in order to use the hook
export const TrackingOverviewPage = () => {
  return (
    <ProvideFermentationAction>
      <TrackingOverview />
    </ProvideFermentationAction>
  );
};
